package org.teddapp.project.ui.backup

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.settings.SupabaseConnector

/**
 * Formulaire de connexion à Supabase.
 *
 *  - Clé anon masquée par défaut, avec un œil pour la révéler.
 *  - « Tester et connecter » appelle connector.connect() ; l'erreur s'affiche sur place,
 *    la clé est effacée du champ une fois enregistrée.
 *  - Déjà connecté : le projet courant, et de quoi le changer ou s'en déconnecter.
 */
@Composable
fun SupabaseConnectForm(
    connector: SupabaseConnector,
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()
    var url by remember { mutableStateOf(connector.currentUrl) }
    var anonKey by remember { mutableStateOf("") }
    var revealKey by remember { mutableStateOf(false) }
    var inFlight by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var successFlash by remember { mutableStateOf(false) }

    val connected = connector.isConnected

    LaunchedEffect(successFlash) {
        if (successFlash) {
            kotlinx.coroutines.delay(2_500L)
            successFlash = false
        }
    }
    // L'adresse pré-remplie suit ce que le connecteur connaît (utile après une reconnexion).
    LaunchedEffect(connector.currentUrl) {
        if (!connected && url.isBlank()) url = connector.currentUrl
    }

    TeddCard(
        modifier = modifier.fillMaxWidth(),
        title = if (connected) "Connecté à Supabase" else "Relier un projet Supabase",
        icon = if (connected) "check" else "globe",
        iconColor = if (connected) TeddTokens.Success else TeddTokens.InkMuted,
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            if (connected) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                        .padding(horizontal = TeddSpace.S3, vertical = 10.dp),
                    verticalArrangement = Arrangement.spacedBy(2.dp),
                ) {
                    Text("PROJET", color = TeddTokens.InkMuted, style = TeddType.Overline)
                    Text(connector.currentUrl, color = TeddTokens.Ink, style = TeddType.MonoSm)
                }
                Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    TeddButton(
                        text = "Changer d'identifiants",
                        onClick = {
                            // Repasser par le formulaire suppose de couper la connexion en cours.
                            connector.disconnect()
                            anonKey = ""
                            errorMessage = null
                        },
                        kind = TeddButtonKind.Secondary,
                    )
                    TeddButton(
                        text = "Déconnecter",
                        onClick = {
                            connector.disconnect()
                            url = ""
                            anonKey = ""
                            errorMessage = null
                        },
                        kind = TeddButtonKind.Danger,
                    )
                }
            } else {
                TeddField(
                    label = "Adresse du projet",
                    value = url,
                    onValueChange = { url = it; errorMessage = null },
                    placeholder = "https://xxxxx.supabase.co",
                )
                TeddField(
                    label = "Clé anon (publique)",
                    value = anonKey,
                    onValueChange = { anonKey = it; errorMessage = null },
                    placeholder = "eyJhbGciOiJIUzI1NiIs…",
                    visualTransformation = if (revealKey) VisualTransformation.None else PasswordVisualTransformation(),
                    trailingContent = {
                        TeddIconButton(onClick = { revealKey = !revealKey }, size = 28.dp) {
                            TeddIcon(name = if (revealKey) "eye-off" else "eye", size = 16.dp, color = TeddTokens.InkMuted)
                        }
                    },
                )
                errorMessage?.let { message ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(TeddTokens.DangerSoft, RoundedCornerShape(TeddTokens.RSm))
                            .padding(horizontal = TeddSpace.S3, vertical = 10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
                    ) {
                        TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Danger)
                        Text(message, color = TeddTokens.Danger, style = TeddType.Caption)
                    }
                }
                TeddButton(
                    text = when {
                        inFlight -> "Connexion…"
                        successFlash -> "Connecté"
                        else -> "Tester et connecter"
                    },
                    onClick = {
                        errorMessage = null
                        inFlight = true
                        scope.launch {
                            val err = connector.connect(url.trim(), anonKey.trim())
                            inFlight = false
                            if (err == null) {
                                successFlash = true
                                anonKey = "" // la clé ne reste pas dans le champ une fois enregistrée
                            } else {
                                errorMessage = err
                            }
                        }
                    },
                    kind = TeddButtonKind.Primary,
                    enabled = url.startsWith("http") && anonKey.length >= 20 && !inFlight,
                    icon = if (successFlash) "check" else null,
                    modifier = Modifier.fillMaxWidth(),
                )
                Text(
                    text = "La clé anon est faite pour les applications clientes. Si plusieurs personnes partagent le projet, " +
                        "protège les tables par des règles Row Level Security.",
                    color = TeddTokens.InkMuted,
                    style = TeddType.Caption,
                )
            }
        }
    }
}
